import { services } from "../services";

// Define types for the actions
interface Action {
  type: string;
  data: any;
}

// Define types for API responses
interface ApiResponse {
  status: number;
  data: {
    data: any;
    pagination?: any;
    message?: string;
  };
  response?: {
    data: {
      message: string;
    };
  };
}

// Define types for parameters
interface CollectionData {
  [key: string]: any;
}

interface UserUpdateParams {
  [key: string]: any;
}

interface PaginationParams {
  [key: string]: any;
  params?: any;
}

// Utility function to manage userId in localStorage
export const userIdStorage = {
  setUserId: (userId: string) => {
    localStorage.setItem("vestin_user_id", userId);
  },
  getUserId: (): string | null => {
    return localStorage.getItem("vestin_user_id");
  },
  removeUserId: () => {
    localStorage.removeItem("vestin_user_id");
  },
};

export const USER_SESSION_RESET = "USER_SESSION_RESET";

export function extractUserIdFromAuth(authData: any): string | null {
  if (!authData) return null;

  const candidates = [
    authData?.data?.user?.id,
    authData?.data?.id,
    authData?.data?._id,
    authData?.data?.user?._id,
    authData?.user?.id,
    authData?.id,
    authData?.data?.details?.id,
  ];

  for (const id of candidates) {
    if (typeof id === "string" && id.length > 0) {
      return id;
    }
  }

  return null;
}

export function clearUserPortfolioState() {
  return { type: USER_SESSION_RESET };
}

export function resetUserSession() {
  return (dispatch: (action: any) => void) => {
    userIdStorage.removeUserId();
    localStorage.removeItem("RwaAuthToken");
    localStorage.removeItem("isLoggedIn");
    dispatch(clearUserPortfolioState());
    dispatch({ type: "AUTH_LOGIN", data: null });
    dispatch({
      type: "FETCH_WEB3_DATA",
      data: { isLoggedIn: false, accounts: [] },
    });
  };
}

export function resetUserSessionForWalletSwitch(accounts: string[] = []) {
  return (dispatch: (action: any) => void) => {
    userIdStorage.removeUserId();
    localStorage.removeItem("RwaAuthToken");
    localStorage.removeItem("isLoggedIn");
    dispatch(clearUserPortfolioState());
    dispatch({ type: "AUTH_LOGIN", data: null });
    dispatch({
      type: "FETCH_WEB3_DATA",
      data: { isLoggedIn: false, accounts },
    });
  };
}

// Utility function to set loading state
export const setPortfolioLoading = (isLoading: boolean) => {
  return {
    type: "IS_LOADER",
    data: { isLoading },
  };
};

// Export the user actions
export const userActions = {
  clearUserPortfolioState,
  resetUserSession,
  resetUserSessionForWalletSwitch,
  extractUserIdFromAuth,
  fetchCategories,
  getProfile,
  getUserNFT,
  getCreators,
  updateUserDetails,
  updateProfileWithFile,
  getMoreCreators,
  createCollection,
  getUserDraftNFT,
  getSingleNFTDetails,
  getLikesCount,
  likeToggler,
  getIsLiked,
  getIsFollow,
  followToggler,
  getLikedNFT,
  getCollectedNFT,
  getCollectionNFT,
  getEditionHistory,
  getNotifications,
  getProfileBanner,
  setLanguage,
  sendInstagramCode,
  getTwitterAccessToken,
  verifyByTwitter,
  getClearNotification,
  getSecondaryMarketPlaceNFT,
  getMoreSecondaryMarketPlaceNFT,
  getSingleSecondaryNFTDetails,
  getSingleRentalPropertyDetails,
  isRewardClaimed,
  getreadNotifications,
  getRentalEarnings,
  getRewardClaimSummary,
  getInvestPoolSummary,
  getTransactionHistory,
  getSecondaryMarketplaceMetrics,
  getRentalSummary,
  getAmenitiesByIds,
};

function fetchedData(type: string, data: any): Action {
  return {
    type: type,
    data: data,
  };
}

function fetchCategories() {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(`/asset-type/`, false)) as ApiResponse;
      if (response.data) {
        dispatch(fetchedData("FETCHED_CATEGORIES", response.data.data));
      }
    } catch (error) {
      console.error("Error in fetchCategories actions:", error);
    }
  };
}

function getProfile(userId: string) {
  return async (dispatch: (action: Action) => void) => {
    try {
      console.log(
        "[API Test] Calling api/v1/user/userDetails?userId=" + userId
      );
      const response = (await services.get(
        `user/userDetails?userId=${userId}`,
        true
      )) as ApiResponse;

      if (response.status === 200) {
        console.log("[API Test] api/v1/user - SUCCESS", response.data);
        dispatch(fetchedData("FETCHED_PROFILE", response.data.data));
      } else {
        console.error(
          "[API Test] api/v1/user - FAILED: Status",
          response.status
        );
      }
    } catch (error) {
      console.error("[API Test] Error fetching profile:", error);
    }
  };
}

function isRewardClaimed(id?: string) {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        `rental/getInvestorPayouts/680b2dc2cd4bc6c5ad06556e`,
        true
      )) as ApiResponse;

      if (response.status === 200) {
        dispatch(fetchedData("IS_REWARD_CLAIMED", response.data));
        return response.data;
      }
    } catch (error) {
      console.error("Error checking reward claim status:", error);
    }
  };
}

function getApiErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as { response?: { data?: { message?: string } } })
      .response;
    if (response?.data?.message) {
      return response.data.message;
    }
  }
  return "Something went wrong. Please try again.";
}

function isSuccessfulApiResponse(
  response: unknown
): response is { status: number; data: { data: unknown } } {
  return (
    !!response &&
    typeof response === "object" &&
    "status" in response &&
    (response as { status: number }).status === 200 &&
    "data" in response
  );
}

function dispatchProfileUpdateResult(
  response: unknown,
  dispatch: (action: Action) => void
): void {
  if (isSuccessfulApiResponse(response)) {
    dispatch(fetchedData("PROFILE_UPDATED", response.data.data));
    return;
  }
  dispatch(fetchedData("API_FAILED", getApiErrorMessage(response)));
}

function updateUserDetails(params: UserUpdateParams) {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = await services.put(`user/update`, params);
      dispatchProfileUpdateResult(response, dispatch);
    } catch (error) {
      console.error("Error updating user details:", error);
      dispatch(fetchedData("API_FAILED", getApiErrorMessage(error)));
    }
  };
}

function updateProfileWithFile(formData: FormData) {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = await services.putFormData(`user/update`, formData);
      dispatchProfileUpdateResult(response, dispatch);
    } catch (error) {
      console.error("Error updating profile with file:", error);
      dispatch(fetchedData("API_FAILED", getApiErrorMessage(error)));
    }
  };
}

function getUserNFT(id: string | null, filter: string) {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        id
          ? `nft/listNftByUser/${id}?status=${filter}`
          : `nft/listNftByUser?status=${filter}`,
        true
      )) as ApiResponse;

      if (response.status === 200) {
        dispatch(fetchedData("FETCHED_USER_NFT", response.data.data));
      }
    } catch (error) {
      console.error("Error fetching user NFT:", error);
    }
  };
}

function createCollection(data: CollectionData) {
  const params = JSON.stringify(data);
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.post(
        "nft/addCollection",
        params
      )) as ApiResponse;
      if (response.status === 200) {
        dispatch(fetchedData("CREATE_COLLECTION", response.data));
      }
    } catch (error) {
      if (error && typeof error === "object" && "response" in error) {
        const apiError = error as { response: { data: any } };
        dispatch(fetchedData("CREATE_COLLECTION", apiError.response.data));
      } else {
        console.error("Error creating collection:", error);
      }
    }
  };
}

function getCreators(params: PaginationParams = {}) {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        `resell/filter`,
        true,
        params
      )) as ApiResponse;

      if (response.status === 200) {
        dispatch(fetchedData("FETCHED_PAGINATION", response.data.pagination));
        dispatch(fetchedData("FETCHED_CREATORS", response.data.data));
      }
    } catch (error) {
      console.error("Error fetching creators:", error);
    }
  };
}

function getMoreCreators(params: PaginationParams = {}) {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        `resell/filter`,
        true,
        params
      )) as ApiResponse;

      if (response.status === 200) {
        dispatch(fetchedData("FETCHED_PAGINATION", response.data.pagination));
        dispatch(fetchedData("FETCHED_MORE_CREATORS", response.data.data));
      }
    } catch (error) {
      console.error("Error fetching more creators:", error);
    }
  };
}

function getUserDraftNFT() {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        `nft/listNftByUser?filter=draft`,
        true
      )) as ApiResponse;

      if (response.status === 200) {
        dispatch(fetchedData("FETCHED_USER_DRAFT_NFT", response.data.data));
      }
    } catch (error) {
      console.error("Error fetching user draft NFT:", error);
    }
  };
}

async function getSingleNFTDetails(id: string): Promise<any | null> {
  try {
    const response = (await services.get(`asset/${id}`, true)) as ApiResponse;

    if (response.status === 200) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error("Error fetching single NFT details:", error);
    return null;
  }
}

function getLikesCount(id: string) {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        `like/getLikesCount/${id}`,
        true
      )) as ApiResponse;

      if (response.status === 200) {
        dispatch(fetchedData("FETCHED_LIKES_COUNT", response.data.data));
      }
    } catch (error) {
      console.error("Error fetching likes count:", error);
    }
  };
}

function likeToggler(id: string) {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        `like/toggle/${id}`,
        true
      )) as ApiResponse;

      if (response.status === 200) {
        getIsLiked(id)(dispatch);
        getLikesCount(id)(dispatch);
      }
    } catch (error) {
      console.error("Error toggling like:", error);
    }
  };
}

function getIsLiked(id: string) {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        `like/isLiked/${id}`,
        true
      )) as ApiResponse;

      if (response.status === 200) {
        dispatch(fetchedData("FETCHED_IS_LIKED", response.data.data));
      }
    } catch (error) {
      console.error("Error checking if liked:", error);
    }
  };
}

function getIsFollow(id: string) {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        `follow/checkIsFollowed/${id}`,
        true
      )) as ApiResponse;

      if (response.status === 200) {
        dispatch(fetchedData("FETCHED_IS_FOLLOW", response.data.data));
      }
    } catch (error) {
      console.error("Error checking if followed:", error);
    }
  };
}

function followToggler(id: string) {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        `follow/toggle/${id}`,
        true
      )) as ApiResponse;

      if (response.status === 200) {
        getIsFollow(id)(dispatch);
      }
    } catch (error) {
      console.error("Error toggling follow:", error);
    }
  };
}

function getLikedNFT(id?: string) {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        id ? `nft/getLikedNfts/${id}` : `nft/getLikedNfts`,
        true
      )) as ApiResponse;

      if (response.status === 200) {
        dispatch(fetchedData("FETCHED_LIKED_NFT", response.data.data));
      }
    } catch (error) {
      console.error("Error fetching liked NFT:", error);
    }
  };
}

function getCollectedNFT(id?: string) {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        id ? `nft/getCollectedNfts/${id}` : `nft/getCollectedNfts`,
        true
      )) as ApiResponse;

      if (response.status === 200) {
        dispatch(fetchedData("FETCHED_COLLECTED_NFT", response.data.data));
      }
    } catch (error) {
      console.error("Error fetching collected NFT:", error);
    }
  };
}

function getRentalEarnings(id?: string) {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        `user/getuserRentalEarningAssets/${id}`,
        true
      )) as ApiResponse;

      if (response.status === 200) {
        dispatch(fetchedData("FETCHED_RENTAL_EARNINGS", response.data.data));
      }
    } catch (error) {
      console.error("Error fetching rental earnings:", error);
    }
  };
}

function getCollectionNFT(id?: string) {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        id ? `nft/listCollection/${id}` : `nft/listCollection`,
        true
      )) as ApiResponse;

      if (response.status === 200) {
        dispatch(fetchedData("FETCHED_COLLECTION_NFT", response.data.data));
      }
    } catch (error) {
      console.error("Error fetching collection NFT:", error);
    }
  };
}

function getEditionHistory(nftId: string, edition: string) {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        `nft/history/${nftId}/${edition}`,
        true
      )) as ApiResponse;

      if (response.status === 200) {
        dispatch(
          fetchedData("FETCHED_NFT_EDITION_HISTORY", response.data.data)
        );
      }
    } catch (error) {
      console.error("Error fetching edition history:", error);
    }
  };
}

function getNotifications() {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        `notification/list`,
        true
      )) as ApiResponse;

      if (response.status === 200) {
        const raw = response.data?.data;
        const list = Array.isArray(raw)
          ? raw.map((n: any) => ({
              id: n._id ?? n.id,
              text: n.text ?? { en: "", tu: "" },
              route: n.route,
              read: n.notificationStatus === "read",
              timestamp: n.createdAt ?? n.timestamp,
              type: n.type,
            }))
          : [];
        dispatch(fetchedData("FETCHED_NOTIFICATIONS", list));
      }
    } catch {
      // Silently handle fetch error; existing notifications remain in state
    }
  };
}

function getClearNotification() {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.put(`notification/clearnotification/`, {
        isDeleted: true,
      })) as ApiResponse;
      // if (response.data) {
      dispatch(fetchedData("FETCHED_PROFILE_BANNERS", response.data.data));
      return response;
      // }
    } catch (error) {
      console.error("Error clearing notification:", error);
    }
  };
}

function getreadNotifications() {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.put(
        `notification/updateStatus`,
        {}
      )) as ApiResponse;

      if (response.data) {
        dispatch(fetchedData("FETCHED_PROFILE_BANNERS", response.data.data));
      }
    } catch (error) {
      console.error("Error updating notification status:", error);
    }
  };
}

function getProfileBanner() {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        `/admin/profile-info/list`,
        true
      )) as ApiResponse;

      if (response.data) {
        dispatch(fetchedData("FETCHED_PROFILE_BANNERS", response.data.data));
        return response;
      }
    } catch (error) {
      console.error("Error fetching profile banner:", error);
    }
  };
}

function setLanguage(lng: string) {
  return (dispatch: (action: Action) => void) => {
    dispatch(fetchedData("SET_LANGUAGE", lng));
  };
}

function sendInstagramCode(code: string) {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.post(`/user/validateInstagram`, {
        code: code,
      })) as ApiResponse;

      if (response.data) {
        dispatch(fetchedData("VERIFIED_BY_INSTAGRAM", response.status));
        return response;
      }
    } catch (error) {
      console.error("Error sending Instagram code:", error);
    }
  };
}

function getTwitterAccessToken() {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        `/user/twitter/access_token`,
        true
      )) as ApiResponse;

      if (response.data) {
        dispatch(fetchedData("TWITTER_ACCESS_TOKEN", response.data.data));
        return response;
      }
    } catch (error) {
      console.error("Error getting Twitter access token:", error);
    }
  };
}

function verifyByTwitter(oauth_token: string, oauth_verifier: string) {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.post(`/user/validateTwitter`, {
        oauth_verifier: oauth_verifier,
        oauth_token: oauth_token,
      })) as ApiResponse;

      if (response.data) {
        dispatch(fetchedData("VERIFIED_BY_TWITTER", response.status));
        return response;
      }
    } catch (error) {
      console.error("Error verifying by Twitter:", error);
    }
  };
}

function getSecondaryMarketPlaceNFT(params: PaginationParams = {}) {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        `resell/filter`,
        true,
        params
      )) as ApiResponse;

      if (response.status === 200) {
        dispatch(fetchedData("FETCHED_PAGINATION", response.data.pagination));
        dispatch(fetchedData("FETCHED_MARKETPLACE", response.data.data));
      }
    } catch (error) {
      console.error("Failed to fetch Secondary NFT details:", error);
    }
  };
}

function getMoreSecondaryMarketPlaceNFT(params: PaginationParams = {}) {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        `resell/filter`,
        true,
        params
      )) as ApiResponse;

      if (response.status === 200) {
        dispatch(fetchedData("FETCHED_PAGINATION", response.data.pagination));
        dispatch(fetchedData("FETCHED_MORE_MARKETPLACE", response.data.data));
      }
    } catch (error) {
      console.error("Failed to fetch Secondary NFT details:", error);
    }
  };
}

async function getSingleRentalPropertyDetails(id: string): Promise<any | null> {
  try {
    const response = (await services.get(
      `rental/property/${id}`,
      true
    )) as ApiResponse;

    if (response.status === 200) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error("Failed to fetch Single Rental Property details:", error);
    return null;
  }
}

async function getSingleSecondaryNFTDetails(id: string): Promise<any | null> {
  try {
    const response = (await services.get(`resell/${id}`, true)) as ApiResponse;

    if (response.status === 200) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error("Failed to fetch Single Secondary NFT details:", error);
    return null;
  }
}

function getRewardClaimSummary(userId: string, period: string = "1month") {
  return async (dispatch: (action: Action) => void) => {
    try {
      dispatch(setPortfolioLoading(true));
      const response = (await services.get(
        `reward-claim/summary/${userId}?period=${period}`,
        true
      )) as ApiResponse;

      if (response.status === 200) {
        dispatch(
          fetchedData("FETCHED_REWARD_CLAIM_SUMMARY", response.data.data)
        );
        return response.data.data;
      }
    } catch (error) {
      console.error("Error fetching reward claim summary:", error);
      dispatch(fetchedData("FETCHED_REWARD_CLAIM_SUMMARY", null));
    } finally {
      dispatch(setPortfolioLoading(false));
    }
  };
}

function getInvestPoolSummary(userId: string) {
  return async (dispatch: (action: Action) => void) => {
    try {
      dispatch(setPortfolioLoading(true));
      const response = (await services.get(
        `user/portfolio-holdings/${userId}?limit=4`,
        true
      )) as ApiResponse;

      if (response.status === 200) {
        const payload = response.data?.data || {};
        const totals = payload?.totals || {};
        const normalizedSummary = {
          ...(payload?.summary || {}),
          totalProperties:
            totals?.totalAssets ?? payload?.summary?.totalProperties ?? 0,
          totalInvestedAmount:
            totals?.totalValue ?? payload?.summary?.totalInvestedAmount ?? 0,
          totalReturnValue:
            totals?.totalReturnValue ?? payload?.summary?.totalReturnValue ?? 0,
          totalReturnPercent:
            totals?.totalReturnPercent ??
            payload?.summary?.totalReturnPercent ??
            0,
          totalCostBasis:
            totals?.totalCostBasis ?? payload?.summary?.totalCostBasis ?? 0,
        };

        const normalizedPayload = {
          ...payload,
          summary: normalizedSummary,
        };

        dispatch(
          fetchedData("FETCHED_INVEST_POOL_SUMMARY", normalizedPayload)
        );
        return normalizedPayload;
      }
    } catch (error) {
      console.error("Error fetching invest pool summary:", error);
      dispatch(fetchedData("FETCHED_INVEST_POOL_SUMMARY", null));
    } finally {
      dispatch(setPortfolioLoading(false));
    }
  };
}

function getTransactionHistory(
  userId: string,
  page: number = 1,
  limit: number = 10
) {
  return async (dispatch: (action: Action) => void) => {
    try {
      dispatch(setPortfolioLoading(true));
      const response = (await services.get(
        `user/transaction-history/${userId}?page=${page}&limit=${limit}`,
        true
      )) as ApiResponse;

      if (response.status === 200) {
        dispatch(
          fetchedData("FETCHED_TRANSACTION_HISTORY", response.data.data)
        );
        return response.data.data;
      }
    } catch (error) {
      console.error("Error fetching transaction history:", error);
      dispatch(fetchedData("FETCHED_TRANSACTION_HISTORY", null));
    } finally {
      dispatch(setPortfolioLoading(false));
    }
  };
}

function getSecondaryMarketplaceMetrics() {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        `resell/summary`,
        true
      )) as ApiResponse;

      if (response.status === 200) {
        dispatch(
          fetchedData(
            "FETCHED_SECONDARY_MARKETPLACE_METRICS",
            response.data.data
          )
        );
        return response.data.data;
      } else {
        console.warn("⚠️ API returned non-200 status:", response.status);
      }
    } catch (error: any) {
      console.error("❌ Error fetching secondary marketplace metrics:", error);
      console.error("❌ Error details:", {
        message: error?.message || "Unknown error",
        status: error?.response?.status,
        data: error?.response?.data,
      });
      dispatch(fetchedData("FETCHED_SECONDARY_MARKETPLACE_METRICS", null));
    }
  };
}

function getRentalSummary() {
  return async (dispatch: (action: Action) => void) => {
    try {
      const response = (await services.get(
        `rental/summary`,
        true
      )) as ApiResponse;

      if (response.status === 200) {
        dispatch(fetchedData("FETCHED_RENTAL_SUMMARY", response.data.data));
        return response.data.data;
      } else {
        console.warn("⚠️ API returned non-200 status:", response.status);
      }
    } catch (error: any) {
      console.error("❌ Error fetching rental summary metrics:", error);
      console.error("❌ Error details:", {
        message: error?.message || "Unknown error",
        status: error?.response?.status,
        data: error?.response?.data,
      });
      dispatch(fetchedData("FETCHED_RENTAL_SUMMARY", null));
    }
  };
}

// Fetch amenities by a list of IDs and store an id->name map in redux
function getAmenitiesByIds(ids: string[]) {
  return async (dispatch: (action: Action) => void) => {
    try {
      if (!Array.isArray(ids) || ids.length === 0) return;
      const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
      const response = (await services.post(`amenity/many`, {
        ids: uniqueIds,
      })) as ApiResponse;

      if (response && (response as any).status === 200) {
        const list = (response as any).data?.data || [];
        const map = list.reduce((acc: Record<string, string>, item: any) => {
          const key = item?._id || item?.id;
          const name = item?.name || item?.title || "";
          if (key) acc[key] = name;
          return acc;
        }, {} as Record<string, string>);

        dispatch({ type: "FETCHED_AMENITIES_BY_ID", data: map });
      }
    } catch (error) {
      console.error("Error fetching amenities by ids:", error);
    }
  };
}
